/**
 * Nado gateway failure → what went wrong, as a code each platform maps to its
 * own copy.
 *
 * - Deliberately not a message: core has no i18n, and the two SDKs namespace
 *   their keys differently (`withdrawal.nado.*` on web, `withdrawNado.*` on RN).
 * - Lives apart from `errors.ts`, which stays flow-agnostic (the codes are
 *   shared across every Nado execute and query).
 */

import {
  NADO_ERROR_CODES,
  NadoGatewayError,
  NadoWithdrawalUnconfirmedError,
  parseNadoExpectedNonce,
} from './errors'

/**
 * Why a withdrawal failed, in terms the user can be told about. Each platform
 * owns an exhaustive map to its own translation keys, so adding a member here
 * fails their builds until they have copy for it.
 */
export type NadoWithdrawalFailure =
  | 'generic'
  | 'network'
  | 'rateLimited'
  | 'exceedsBorrowLimit'
  | 'invalidNonce'
  | 'noPriorDeposit'
  | 'withdrawRisk'
  /** Outcome unknown — the copy for this one must never invite a retry. */
  | 'unconfirmed'

/**
 * Failures the user recovers from by retrying, lowering the amount, depositing
 * first, or waiting. `unconfirmed` is absent on purpose: it is not recovered,
 * and it is already logged at ERROR where it is raised.
 */
const RECOVERABLE_FAILURES: ReadonlySet<NadoWithdrawalFailure> = new Set([
  'network',
  'rateLimited',
  'exceedsBorrowLimit',
  'invalidNonce',
  'noPriorDeposit',
  'withdrawRisk',
])

export function toNadoWithdrawalFailure(error: unknown): NadoWithdrawalFailure {
  if (error instanceof NadoWithdrawalUnconfirmedError) {
    return 'unconfirmed'
  }
  if (!(error instanceof NadoGatewayError)) {
    return 'generic'
  }
  if (parseNadoExpectedNonce(error) !== undefined) {
    return 'invalidNonce'
  }
  if (error.kind === 'transport' || error.kind === 'timeout') {
    return 'network'
  }
  if (error.httpStatus === 429) {
    return 'rateLimited'
  }
  switch (error.errorCode) {
    case NADO_ERROR_CODES.SPOT_EXECUTE_EXCEEDS_BORROW_LIMIT:
      return 'exceedsBorrowLimit'
    case NADO_ERROR_CODES.INVALID_NONCE:
      return 'invalidNonce'
    case NADO_ERROR_CODES.NO_PRIOR_DEPOSIT:
      return 'noPriorDeposit'
    case NADO_ERROR_CODES.WITHDRAW_RISK:
      return 'withdrawRisk'
    default:
      return 'generic'
  }
}

/**
 * Whether the failure is one we have specific copy for — the input to log
 * severity (recovered → WARN, gave up → ERROR).
 *
 * Derived from {@link toNadoWithdrawalFailure} rather than its own code set, so
 * severity and messaging cannot drift apart.
 */
export function isExpectedNadoWithdrawalFailure(error: unknown): boolean {
  if (!(error instanceof NadoGatewayError)) {
    return false
  }
  return RECOVERABLE_FAILURES.has(toNadoWithdrawalFailure(error))
}
